import { Dispatch, FormEvent, ForwardedRef, SetStateAction, useEffect, useRef, useState } from "react";

import { FileUploadHandle, LayeredSelectData, SelectData, UploadedDocument } from "../../../../../models";
import { uploadDealDocument } from "../../../../../service/fund";
import { searchDealDocuments } from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { useIsLoadingWrapper } from "../../../../../utils";
import { Toast } from "../../../../../widget";

export const ADD_NEW_DOCUMENT_TYPE_OPTION = "Add new type...";

interface UseUploadDealDocumentModalProps {
  show: boolean;
  closeModal: () => void;
  dealUuid: string;
  dealDocumentUuid?: string;
  customVariant?: string;
  documentType?: string;
  refreshDocuments?: boolean;
  setRefreshDocuments?: Dispatch<SetStateAction<boolean>>;
  // Reports the documents an upload created, for callers that link them elsewhere (the task overlay).
  onUploaded?: (documents: UploadedDocument[]) => void;
}

interface UseUploadDealDocumentModalReturnData {
  fileUploadRef: ForwardedRef<FileUploadHandle>;
  dealDocumentUuid?: string;
  customVariant?: string;
  documentType?: string;
  documentTypes?: LayeredSelectData;
  files?: File[];
  isMultipleUpload: boolean;
  errors: ServiceError[];
  isHandleSubmitLoading: boolean;
  setDealDocumentUuid: Dispatch<SetStateAction<string | undefined>>;
  setCustomVariant: Dispatch<SetStateAction<string | undefined>>;
  setDocumentType: Dispatch<SetStateAction<string | undefined>>;
  setFiles: Dispatch<SetStateAction<File[] | undefined>>;
  setIsMultipleUpload: Dispatch<SetStateAction<boolean>>;
  clearFileInput: () => void;
  handleCancel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

const getInitialValue = (value?: string): string | undefined => (value && value.length > 0 ? value : undefined);

export const useUploadDealDocumentModal = ({
  show,
  closeModal,
  dealUuid,
  dealDocumentUuid: dealDocumentUuidProp,
  customVariant: customVariantProp,
  documentType: documentTypeProp,
  refreshDocuments,
  setRefreshDocuments,
  onUploaded,
}: UseUploadDealDocumentModalProps): UseUploadDealDocumentModalReturnData => {
  const fileUploadRef = useRef<FileUploadHandle>(null);
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [dealDocumentUuid, setDealDocumentUuid] = useState<string | undefined>(getInitialValue(dealDocumentUuidProp));
  const [customVariant, setCustomVariant] = useState<string | undefined>(getInitialValue(customVariantProp));
  const [documentType, setDocumentType] = useState<string | undefined>(getInitialValue(documentTypeProp));
  const [documentTypes, setDocumentTypes] = useState<LayeredSelectData>();
  const [files, setFiles] = useState<File[]>();
  const [isMultipleUpload, setIsMultipleUpload] = useState(false);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const clearFileInput = (): void => {
    if (fileUploadRef.current) fileUploadRef.current.clearInput();
  };

  const handleCancel = (): void => {
    clearFileInput();
    setDealDocumentUuid(getInitialValue(dealDocumentUuidProp));
    setCustomVariant(getInitialValue(customVariantProp));
    setDocumentType(getInitialValue(documentTypeProp));
    setFiles(undefined);
    setIsMultipleUpload(false);
    setErrors([]);
    closeModal();
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    await uploadDealDocument({
      customVariant: customVariant || "",
      dealDocumentUuid: dealDocumentUuid || "",
      dealUuid,
      documentType: documentType || "",
      files: files || [],
    }).then((response) => {
      if (response.status === Status.Success) {
        const uploadedFileNames = files?.map((file) => file.name) ?? [];

        Toast.success({
          message: uploadedFileNames.length > 1 ? "Documents uploaded successfully" : "Document uploaded successfully",
        });

        /*
          Unlike the project upload, this endpoint names the documents it created — one per file, since a
          multi-file upload writes a deal document per file — so no lookup is needed to report them.
        */
        if (onUploaded) {
          const uploadedDocuments = (response.data?.documents ?? []).map((document) => ({
            uuid: document.uuid,
            fileName: document.fileName,
          }));

          /*
            A file the response did not name cannot be acted on, so nothing attaches it — the task overlay would
            attach nothing and show no failure. The upload itself stands, so re-submitting is not the retry (it
            would upload a second document); the attach is what has to be reported, named, so the user knows
            which file it was and does not assume it was attached.
          */
          const unattachedFileNames = uploadedFileNames.filter(
            (fileName) => !uploadedDocuments.some((document) => document.fileName === fileName)
          );

          if (unattachedFileNames.length > 0) {
            Toast.error({
              message: `${
                unattachedFileNames.length > 1 ? "These documents were" : "The document was"
              } uploaded, but could not be attached: ${unattachedFileNames.join(", ")}`,
            });
          }

          if (uploadedDocuments.length > 0) {
            onUploaded(uploadedDocuments);
          }
        }
        if (setRefreshDocuments) {
          setRefreshDocuments(!refreshDocuments);
        }
        handleCancel();
      }

      if (response.status === Status.Error && response.errors) {
        setErrors(
          response.errors.map((error) => {
            if (error.code === "VALIDATION_ATTRIBUTE_REQUIRED" && error.property) {
              return { ...error, message: `${error.property} is a required field` };
            }
            return { ...error, message: getErrorMessageFromCode(error.code) };
          })
        );
      }
    });
  }, setIsHandleSubmitLoading);

  useEffect(() => {
    setDealDocumentUuid(getInitialValue(dealDocumentUuidProp));
    setCustomVariant(getInitialValue(customVariantProp));
    setDocumentType(getInitialValue(documentTypeProp));
  }, [dealDocumentUuidProp, customVariantProp, documentTypeProp]);

  useEffect(() => {
    let isActive = true;

    if (!dealUuid || !show) {
      return () => {
        isActive = false;
      };
    }

    const fetchDocumentTypes = async (): Promise<void> => {
      setDocumentTypes(undefined);
      const documentTypeSet = new Set<string>();

      const fetchPage = async (afterCursor: string | null, guard: number): Promise<void> => {
        if (guard >= 10) return;

        const response = await searchDealDocuments({
          dealUuid,
          paging: {
            afterCursor,
            beforeCursor: null,
            limit: 50,
          },
          sort: [
            {
              key: "results.createdAt",
              direction: "desc",
            },
          ],
        });

        if (response.status !== Status.Success || !response.data?.results) {
          return;
        }

        response.data.results.forEach((document) => {
          const type = document.documentType;
          if (type && type.trim()) {
            documentTypeSet.add(type);
          }
        });

        const hasNextPage = response.data.paging?.hasNextPage ?? false;
        const nextCursor = response.data.paging?.endCursor ?? null;
        if (hasNextPage && nextCursor) {
          await fetchPage(nextCursor, guard + 1);
        }
      };

      await fetchPage(null, 0);

      if (!isActive) return;

      const documentTypeValues = Array.from(documentTypeSet);
      const documentTypeOptions = documentTypeValues.map((type) => ({ key: type, value: type }));
      if (documentTypeOptions.length > 0) {
        documentTypeOptions.push({
          key: ADD_NEW_DOCUMENT_TYPE_OPTION,
          value: ADD_NEW_DOCUMENT_TYPE_OPTION,
        });
      }
      setDocumentTypes(
        documentTypeOptions.length > 0
          ? [
              {
                optionsHeader: "Document types",
                options: documentTypeOptions as SelectData,
              },
            ]
          : undefined
      );
    };

    fetchDocumentTypes();

    return () => {
      isActive = false;
    };
  }, [dealUuid, refreshDocuments, show]);

  return {
    fileUploadRef,
    dealDocumentUuid,
    customVariant,
    documentType,
    documentTypes,
    files,
    isMultipleUpload,
    errors,
    isHandleSubmitLoading,
    setDealDocumentUuid,
    setCustomVariant,
    setDocumentType,
    setFiles,
    setIsMultipleUpload,
    clearFileInput,
    handleCancel,
    handleSubmit,
  };
};
